// The thin controls' value meter: a fixed-length track whose fill length
// reads as the post's value, like a health bar. Its length follows the value
// itself on a log scale (the text width does not: "$0.007" is wider than
// "$0.01"), so posts compare at a glance down a thread.
//
// The seed share of the value is drawn as a denser tint than the upvalue share,
// feathered into it, rather than by hue: brand-seed and the USD money colour
// are the same green, so a hue split would vanish in USD.

// The scale runs from 1K sats (empty) to 10M sats (full): the band where posts'
// values actually spread, so ordinary posts do not all sit near the middle.
// Any value at all shows at least a sliver.
export const VALUE_METER_EMPTY_SATS = 1_000
export const VALUE_METER_FULL_SATS = 10_000_000
const MIN_FILL_PERCENT = 6

// How far the seed/upvalue boundary is feathered, in % of the fill.
const SEED_FEATHER = 14

export function valueMeterFillPercent(totalSats: number): number {
    if (!(totalSats > 0)) return 0
    const low = Math.log10(VALUE_METER_EMPTY_SATS)
    const fraction = (Math.log10(totalSats) - low) / (Math.log10(VALUE_METER_FULL_SATS) - low)
    const percent = Math.min(1, Math.max(0, fraction)) * 100
    return Math.round(Math.max(MIN_FILL_PERCENT, percent) * 10) / 10
}

export function valueMeterSeedSharePercent(totalSats: number, seedSats: number): number {
    if (!(totalSats > 0) || !(seedSats > 0)) return 0
    return Math.round(Math.min(1, seedSats / totalSats) * 1000) / 10
}

// The fill's background: the seed tint from the left, feathered into the
// upvalue tint at the seed share. Colours come from CSS custom properties the
// stylesheet defines (--value-meter-seed, --value-meter-upvalue).
export function valueMeterFillBackground(seedSharePercent: number): string {
    const seed = 'var(--value-meter-seed)'
    const upvalue = 'var(--value-meter-upvalue)'
    if (seedSharePercent >= 100) return seed
    if (seedSharePercent <= 0) return upvalue
    const from = Math.max(0, seedSharePercent - SEED_FEATHER)
    const to = Math.min(100, seedSharePercent + SEED_FEATHER)
    return `linear-gradient(90deg, ${seed} 0%, ${seed} ${from}%, ${upvalue} ${to}%, ${upvalue} 100%)`
}
